import { attempt } from 'es-toolkit/util';
import { LOCAL_STORAGE_KEY } from '../../localStorage';
import {
  MIGRATION_VERSION_KEY,
  MIGRATIONS,
} from './defaultMigrations.constants';

/**
 * Versioned localStorage migrations for map-layer default changes.
 *
 * When a layer ships behind a release flag with a default value, and that
 * default later changes once the flag is enabled, existing users will have
 * the stale default persisted in localStorage. `mergeMapLayersValue` fills
 * in missing keys but cannot fix keys that already exist with a stale value.
 *
 * This system patches stale values directly in localStorage before the
 * Recoil atom reads them. Each migration maps layer keys to their corrected
 * defaults. Migrations run sequentially from the user's current version, and
 * a version number is persisted so each migration runs at most once.
 *
 * To add a new migration, append an entry to the MIGRATIONS array in
 * defaultMigrations.constants.ts.
 */

const STORAGE_KEYS = [
  LOCAL_STORAGE_KEY.MAP_LAYERS,
  LOCAL_STORAGE_KEY.MAP_LAYERS_PROGRESSION_MODEL,
];

export const runMapLayerDefaultMigrations = (): void => {
  const currentVersion = parseInt(
    localStorage.getItem(MIGRATION_VERSION_KEY) ?? '0',
    10,
  );

  // Already up-to-date — nothing to do.
  if (currentVersion >= MIGRATIONS.length) return;

  STORAGE_KEYS.forEach((key) => {
    const raw = localStorage.getItem(key);
    if (!raw) return;

    const [parseError, parsed] = attempt(() => JSON.parse(raw));
    if (parseError) return;

    // Legacy string[] format is handled by mergeMapLayersValue — skip it.
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') return;

    // Apply all missing migrations sequentially.
    for (let i = currentVersion; i < MIGRATIONS.length; i += 1) {
      Object.assign(parsed, MIGRATIONS[i]);
    }

    localStorage.setItem(key, JSON.stringify(parsed));
  });

  localStorage.setItem(MIGRATION_VERSION_KEY, MIGRATIONS.length.toString());
};
